// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { Call, CallAgent, StartCallOptions } from '@azure/communication-calling';
import { CommunicationIdentifier } from '@azure/communication-common';
import { _toCommunicationIdentifier } from '@internal/acs-ui-common';
import { StatefulCallClient, StatefulDeviceManager } from '@internal/calling-stateful-client';
import memoizeOne from 'memoize-one';
import { isACSCallParticipants } from '../utils/callUtils';
import { createDefaultCommonCallingHandlers, CommonCallingHandlers } from './createCommonHandlers';
/* @conditional-compile-remove(video-background-effects) */
import { VideoBackgroundEffectsDependency } from './createCommonHandlers';

/**
 * Object containing all the handlers required for calling components.
 *
 * Calling related components from this package are able to pick out relevant handlers from this object.
 * See {@link useHandlers} and {@link usePropsFor}.
 *
 * @public
 */
export interface CallingHandlers extends CommonCallingHandlers {
  onStartCall: (participants: CommunicationIdentifier[], options?: StartCallOptions) => Call | undefined;
}

/* @conditional-compile-remove(video-background-effects) */
/**
 * Configuration options to include video effect background dependency.
 * @public
 */
export type CallingHandlersOptions = {
  onResolveVideoBackgroundEffectsDependency?: () => Promise<VideoBackgroundEffectsDependency>;
};

/**
 * Type of {@link createDefaultCallingHandlers}.
 *
 * @public
 */
export type CreateDefaultCallingHandlers = (callClient: StatefulCallClient, callAgent: CallAgent | undefined, deviceManager: StatefulDeviceManager | undefined, call: Call | undefined, /* @conditional-compile-remove(video-background-effects) */
options?: CallingHandlersOptions) => CallingHandlers;

/**
 * Create the default implementation of {@link CallingHandlers} for teams call.
 *
 * Useful when implementing a custom component that utilizes the providers
 * exported from this library.
 *
 * @public
 */
export const createDefaultCallingHandlers: CreateDefaultCallingHandlers = memoizeOne((...args) => {
  const [callClient, callAgent, deviceManager, call, /* @conditional-compile-remove(video-background-effects) */options] = args;
  return {
    ...createDefaultCommonCallingHandlers(callClient, deviceManager, call, /* @conditional-compile-remove(video-background-effects) */options),
    // FIXME: onStartCall API should use string, not the underlying SDK types.
    onStartCall: (participants: CommunicationIdentifier[], options?: StartCallOptions): Call | undefined => {
      if (!isACSCallParticipants(participants)) {
        throw new Error('TeamsUserIdentifier in Teams call is not supported!');
      }
      return callAgent?.startCall(participants, options);
    },
    onRemoveParticipant: async (userId: string): Promise<void> => {
      const participant = _toCommunicationIdentifier(userId);
      await call?.removeParticipant(participant);
    }
  };
});"../../../acs-ui-common/src""../../../calling-stateful-client/src"